/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/no-identical-functions */
import ExcelJS from 'exceljs'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { saveAs } from 'file-saver'

import useSnackbar from '../components/organisms/Snackbar/useSnackbar'

export const BASE_FILE_UPLOAD_EXTENSIONS = ['.xlsx']

export const validateExpectedLabel = (rows, row, column, label, error, t) => {
  const check = typeof label === 'string' ? [label] : label

  const errorMessage = t(error, {
    attribute: check.toString(),
    position: getCellCode(row, column),
  })

  if (
    !rows[row] ||
    !rows[row][column]
      ?.toString()
      .trim()
      .toLowerCase()
      .match(new RegExp(check.join('|'), 'i'))
  ) {
    throw new Error(errorMessage)
  }
  return true
}

export const validateNotEmpty = (rows, row, column, t) => {
  if (
    !rows[row] ||
    !rows[row][column] ||
    !rows[row][column].toString().trim()
  ) {
    // throw new Error(
    //   t('common.error.fileUpload.emptyCell', {
    //     position: getCellCode(row, column),
    //   })
    // )
    return false
  }
  return true
}

export const getCellCode = (row, column) => {
  const prefix = Math.floor((column - 1) / 26)
  const char = (column - 1) % 26
  return `${
    prefix ? String.fromCharCode(64 + prefix) : ''
  }${String.fromCharCode(65 + char)}${row}`
}

const defaultParser = (rows, t) => {
  validateExpectedLabel(
    rows,
    1,
    1,
    t('common.excel.plate'),
    'common.error.fileUpload.wrongColumnName',
    t
  )

  return rows
    .slice(2)
    .map((row) =>
      row.length && row[1]?.toString().trim()
        ? { plate: row[1].toString().trim().toUpperCase() }
        : null
    )
    .filter((item) => item)
}

const useInputOutput = (
  updateCallback = (id, data, fileName, fetchAdditional) => {},
  parser = defaultParser,
  accept = BASE_FILE_UPLOAD_EXTENSIONS
) => {
  const { showSnackbarMessage } = useSnackbar()
  const { t } = useTranslation()

  const readFileAndSendData = useCallback(
    (file, fetchAdditional) => {
      const reader = new FileReader()
      const workbook = new ExcelJS.Workbook()
      reader.onerror = () =>
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fileUpload.failedReading'),
        })
      reader.onload = async () => {
        try {
          await workbook.xlsx.load(reader.result)
        } catch (error) {
          showSnackbarMessage({
            severity: 'error',
            message: t('common.error.fileUpload.failedReading'),
          })
          return
        }

        try {
          const data = parser(workbook.worksheets[0].getSheetValues(), t)

          updateCallback && updateCallback(data, file.name, fetchAdditional)
        } catch (error) {
          showSnackbarMessage({
            severity: 'error',
            message: error.message,
          })
        }
      }
      reader.readAsArrayBuffer(file)
    },
    [parser, showSnackbarMessage, t, updateCallback]
  )

  const handleDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 1) {
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fileUpload.onlyOne'),
        })
        return
      }
      if (rejectedFiles.length > 0) {
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fileUpload.wrongExtension', {
            attribute: accept.toString(),
          }),
        })
        return
      }
      acceptedFiles.forEach((file, index) => {
        readFileAndSendData(file)
      })
    },
    [accept, readFileAndSendData, showSnackbarMessage, t]
  )

  const handleSelectFile = (e) => {
    if (e.target.files.length) {
      const file = e.target.files[0]
      const extValid = accept?.some(
        (ext) =>
          file.name.substr(file.name.length - ext.length, ext.length) === ext
      )

      if (!extValid) {
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fileUpload.wrongExtension', {
            attribute: accept?.toString(),
          }),
        })
      } else {
        readFileAndSendData(file)
      }
      e.target.value = null
    }
  }

  const saveXls = async (name, rows) => {
    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet()

    sheet.addRows(rows)

    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      saveAs(blob, `${name}.xlsx`)
    })
  }

  const saveXlsMultiSheets = async (name, worksheets = []) => {
    const workbook = new ExcelJS.Workbook()
    worksheets.forEach((worksheet, index) => {
      const sheet = workbook.addWorksheet(worksheet.name)
      if (
        worksheet.hasOwnProperty('columns') &&
        Array.isArray(worksheet.columns)
      ) {
        sheet.addRow(worksheet.columns)
      }
      sheet.addRows(worksheet.rows)
    })
    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      saveAs(blob, `${name}.xlsx`)
    })
  }

  return {
    handleDrop,
    handleSelectFile,
    readFileAndSendData,
    saveXls,
    saveXlsMultiSheets,
  }
}

export default useInputOutput
